import * as moment from "moment-timezone";
import * as React from "react";
import { useMappedState } from "redux-react-hook";

import {
  getDeliveryWeekLeadTimes,
  getOrderMode
} from "src/core/drops/utils/inherited-settings";
import { IStore } from "src/core/shared/store/index";
import useAllowedLeadTimeGroups from "src/core/orders/hooks/validation/useAllowedLeadTimeGroups";
import { useDrop } from "src/core/drops/hooks";
import { ListProductsFilters } from "src/core/products/api/products.api";

const useProductSearchFilters = () => {
  const [ { drop } ] = useDrop();

  const {
    currentOrder, currentList, currentRole, installedModules
  } = useMappedState(({
    orders, global, lists
  }: IStore) => ({
    currentOrder: orders.currentOrder,
    currentList: lists.currentList,
    currentRole: global.role,
    installedModules: global.settings ? global.settings.installedModules || [] : []
  }));

  const getLeadTimeGroupsAllowed = useAllowedLeadTimeGroups();

  return React.useCallback(
    (showUnavailable = false) => {
      let dateLedOnly = false;

      if (drop) {
        const orderMode = getOrderMode(drop.groupedSettings);

        if (orderMode && orderMode.allow === "DL") {
          dateLedOnly = true;
        }
      }
      const currentOrderMode = currentOrder?.mode ?? currentList?.mode;
      const newFilters: ListProductsFilters = {};
      const now = moment.utc().toDate();

      newFilters.published = true;

      if (drop && currentOrder.mode === "edit") {
        if (!showUnavailable) {
          const leadTimes = getDeliveryWeekLeadTimes(drop.groupedSettings);

          if (currentOrder.data?.orderDeliveryDate && leadTimes) {
            const allowedCutoffDays = getLeadTimeGroupsAllowed(currentOrder.data.orderDeliveryDate);

            newFilters.leadTimeGroupId = allowedCutoffDays;
          }
        }
      } else {
        if (currentRole && currentRole.type === "admin") {
          delete newFilters.published;
        }
      }

      // if price change module then filter with effective date too
      if (installedModules.includes("price-change") && drop) {
        newFilters.priceCodeId = drop.priceCodeId;

        if (installedModules.includes("product-approval") && drop.productApprovalListId) {
          newFilters.productApprovalListId = drop.productApprovalListId;
        }

        if (installedModules.includes("special-price") && drop.specialPriceCodeId) {
          newFilters.specialPriceCodeId = drop.specialPriceCodeId;
        }

        if (currentOrder.data?.orderDeliveryDate && currentOrderMode === "edit") {
          newFilters.effectiveDate = currentOrder.data.orderDeliveryDate || now;
        }
      } else if (drop) {
        // otherwise just use pricecodes
        newFilters.branchId = drop.id;
      }

      if ((currentOrder.data?.orderDeliveryDate) || (currentList.data?.schedule) || dateLedOnly) {
        newFilters.madeToOrder = false;
      }

      if (currentRole?.code === "customer-admin") {
        delete newFilters.branchId;

        if (currentOrder.data?.branchId && currentOrder.mode === "edit") {
          newFilters.branchId = currentOrder.data.branchId;
        }

        if (currentList.data?.branchId && currentList.mode === "edit") {
          newFilters.branchId = currentList.data.branchId;
        }
      }

      return newFilters;
    },
    [
      drop,
      currentOrder.data?.orderDeliveryDate,
      currentOrder.mode,
      currentList.mode,
      currentList.data?.schedule,
      currentList.data?.branchId,
      currentOrder.data?.branchId,
      installedModules,
      currentRole,
      getLeadTimeGroupsAllowed
    ]
  );
};

export default useProductSearchFilters;
